import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { ITemplateCard } from '../../viewmodels/AppViewModels';

export const SOCIAL_DRAFTS_QUERY_KEY = 'socialDrafts';

export function useSocialDrafts(params: {
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
	forUserId?: string | null;
}) {
	const userSession = useUserSession();

	return useQuery({
		enabled: params.enabled,
		queryFn: async () => {
			let urlPath: string;
			const queryParams: Record<string, string> = {};

			const targetUserId = params.forUserId;

			if (targetUserId) {
				// Use specific user endpoint with forUserId parameter
				urlPath = 'social/post/drafts';
				queryParams.forUserId = targetUserId;
			} else {
				// Use all users endpoint (admin mode)
				urlPath = 'social/post/drafts/all';
			}

			// Always use ImpersonationBroker when impersonationContext exists
			// The forUserId parameter works with impersonation for AllowAccountAdminForUser filter
			// When a targetUserId is provided, enhance the impersonationContext to include user info
			// so that ImpersonationBroker automatically adds impUserId parameter
			const enhancedImpersonationContext =
				targetUserId && params.impersonationContext
					? {
							...params.impersonationContext,
							user: { id: targetUserId },
						}
					: params.impersonationContext;

			const url = Api.ImpersonationBroker.composeApiUrl({
				urlPath,
				queryParams,
				impersonationContext: enhancedImpersonationContext,
			});

			const result = await userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.ISocialMediaPost>>(
				url,
				'GET'
			);

			// Transform social media posts to template cards format
			const posts = result.values || [];
			return posts.map(
				(post): ITemplateCard => ({
					id: post.id,
					isDraft: true,
					name: post.name || 'Untitled Social Media Post',
					summary: typeof post.content === 'string' ? post.content : '',
					content: post.content,
					schedule: null,
					associatedTemplates: null,
					attachments: post.attachments,
					creator: null, // Creator info not available in ISocialMediaPost interface
				})
			);
		},
		queryKey: [SOCIAL_DRAFTS_QUERY_KEY, params.forUserId, params.impersonationContext],
		refetchOnWindowFocus: false,
	});
}
